/**
 * AdminUsers — Admin-only directory of all users with per-user audit
 * histories. Lets admins:
 *   - Search/filter the user list (by email/name, plan)
 *   - Click any user to expand and see all of their audits
 *   - Click any audit to open the usual detail page (admin override
 *     in the backend allows reading another user's audit; the detail
 *     page shows a red "Viewing as admin" banner)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowSquareOut, CaretDown, CaretRight, Crown, MagnifyingGlass, ShieldCheck, Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { fmtDate, scoreClass, shortUrl } from "@/lib/score-utils";

const PLAN_COLOR = {
  free:   "text-ink-muted",
  trial:  "text-warning",
  pro:    "text-brand",
  agency: "text-success",
};

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [expanded, setExpanded] = useState({}); // userId → audits[] | "loading" | null
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (planFilter) params.plan = planFilter;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data.users);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load users");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // initial

  const onSearch = (e) => { e.preventDefault(); load(); };

  const toggle = async (u) => {
    if (expanded[u.id]) {
      setExpanded({ ...expanded, [u.id]: null });
      return;
    }
    setExpanded({ ...expanded, [u.id]: "loading" });
    try {
      const { data } = await api.get(`/admin/users/${u.id}/audits`);
      setExpanded((cur) => ({ ...cur, [u.id]: data.audits }));
    } catch (err) {
      toast.error("Failed to load user audits");
      setExpanded((cur) => ({ ...cur, [u.id]: null }));
    }
  };

  return (
    <AppShell>
      <div className="label-overline mb-2">Admin</div>
      <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter leading-none mb-2">
        Users
      </h1>
      <p className="text-sm text-ink-muted mb-8 max-w-2xl">
        Every account on this Auditor install. Click any row to expand
        their audit history. As super admin you can open any audit for
        support or oversight — the report page will show a banner so
        you never forget you&apos;re viewing someone else&apos;s data.
      </p>

      {/* Search + filters */}
      <form onSubmit={onSearch} className="flex flex-wrap gap-2 mb-6" data-testid="admin-users-toolbar">
        <div className="relative flex-1 min-w-[260px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email or name"
            data-testid="admin-users-search"
            className="w-full bg-white border border-ink rounded-sm pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand mono"
          />
        </div>
        <select
          value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}
          data-testid="admin-users-plan-filter"
          className="bg-white border border-ink rounded-sm px-3 py-2 text-sm">
          <option value="">All plans</option>
          <option value="free">Free</option>
          <option value="trial">Trial</option>
          <option value="pro">Pro</option>
          <option value="agency">Agency</option>
        </select>
        <button type="submit" data-testid="admin-users-apply"
          className="bg-ink text-white px-4 py-2 rounded-sm font-semibold text-sm hover:bg-brand transition-colors">
          Apply
        </button>
      </form>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Tile icon={Users}      label="Total users" value={users.length} testId="admin-users-stat-total" />
        <Tile icon={Crown}      label="Admins"      value={users.filter((u) => u.is_admin).length} />
        <Tile icon={ShieldCheck} label="Paid"       value={users.filter((u) => u.plan === "pro" || u.plan === "agency").length} />
        <Tile icon={MagnifyingGlass} label="Audits (sum)" value={users.reduce((s, u) => s + (u.audits || 0), 0)} />
      </div>

      <div className="surface-card overflow-hidden" data-testid="admin-users-table">
        <div className="grid grid-cols-[40px_2fr_1fr_0.7fr_0.7fr_0.9fr] bg-secondary border-b border-border label-overline text-[10px]">
          <div className="px-3 py-2.5" />
          <div className="px-4 py-2.5">User</div>
          <div className="px-4 py-2.5">Plan</div>
          <div className="px-4 py-2.5">Audits</div>
          <div className="px-4 py-2.5">Role</div>
          <div className="px-4 py-2.5">Joined</div>
        </div>

        {loading && <div className="px-6 py-8 text-sm text-ink-muted">Loading…</div>}
        {!loading && users.length === 0 && (
          <div className="px-6 py-12 text-sm text-ink-muted text-center">No users match.</div>
        )}

        {users.map((u) => {
          const rows = expanded[u.id];
          return (
            <div key={u.id} className="border-b border-border last:border-0">
              <button
                onClick={() => toggle(u)}
                data-testid={`admin-user-row-${u.id}`}
                className="w-full grid grid-cols-[40px_2fr_1fr_0.7fr_0.7fr_0.9fr] items-center text-left hover:bg-secondary/40 transition-colors text-sm"
              >
                <div className="px-3 py-3 text-ink-muted">
                  {rows ? <CaretDown size={14} /> : <CaretRight size={14} />}
                </div>
                <div className="px-4 py-3 min-w-0">
                  <div className="font-semibold truncate">{u.name}</div>
                  <div className="text-xs text-ink-muted mono truncate">{u.email}</div>
                </div>
                <div className={`px-4 py-3 mono text-xs uppercase tracking-wider font-bold ${PLAN_COLOR[u.plan] || ""}`}>
                  {u.plan}
                </div>
                <div className="px-4 py-3 num-display font-bold tabular-nums">{u.audits}</div>
                <div className="px-4 py-3">
                  {u.is_admin ? (
                    <span className="inline-flex items-center gap-1 mono text-[10px] tracking-wider px-1.5 py-0.5 bg-warning text-white rounded-sm font-bold">
                      <Crown size={10} weight="fill" /> ADMIN
                    </span>
                  ) : (
                    <span className="text-xs text-ink-muted">—</span>
                  )}
                </div>
                <div className="px-4 py-3 mono text-xs text-ink-muted">{fmtDate(u.created_at)}</div>
              </button>

              {rows && rows !== "loading" && (
                <div className="bg-secondary/40 border-t border-border" data-testid={`admin-user-audits-${u.id}`}>
                  {rows.length === 0 ? (
                    <div className="px-6 py-6 text-xs text-ink-muted text-center">No audits yet.</div>
                  ) : (
                    rows.slice(0, 50).map((a) => (
                      <Link
                        key={a.id} to={`/app/audit/${a.id}`}
                        data-testid={`admin-user-audit-${a.id}`}
                        className="grid grid-cols-[40px_2fr_0.7fr_0.7fr_0.9fr_auto] items-center px-4 py-2.5 border-b border-border/50 last:border-0 hover:bg-white transition-colors text-sm"
                      >
                        <div />
                        <div className="px-2 mono text-xs truncate">{shortUrl(a.url)}</div>
                        <div className="px-2">
                          <span className={`num-display text-lg font-bold tabular-nums ${scoreClass(a.scores?.overall)}`}>
                            {a.scores?.overall ?? "—"}
                          </span>
                        </div>
                        <div className="px-2 mono text-[10px] uppercase tracking-wider text-ink-muted">
                          {a.status}
                        </div>
                        <div className="px-2 mono text-xs text-ink-muted">{fmtDate(a.created_at)}</div>
                        <div className="px-2 text-ink-muted">
                          <ArrowSquareOut size={12} />
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              )}
              {rows === "loading" && (
                <div className="bg-secondary/40 px-6 py-4 text-xs text-ink-muted">Loading audits…</div>
              )}
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}

function Tile({ icon: Icon, label, value, testId }) {
  return (
    <div className="surface-card p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="label-overline">{label}</div>
        <Icon size={14} className="text-ink-muted" />
      </div>
      <div className="num-display text-3xl font-black tabular-nums">{value}</div>
    </div>
  );
}
